import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import RateReviewIcon from "@mui/icons-material/RateReview";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
    Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel,
    List, ListItemButton, ListItemIcon, ListItemText,
    MenuItem, Paper, Pagination, Select, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import ConfirmDialog from "../components/ConfirmDialog";
import KeywordBox from "../components/KeywordBox";
import PreviewRefreshJobDialog from "../pzl_common/PreviewRefreshJobDialog";
import {
    adminTagFilterAutocompleteSlotProps,
    adminTagFilterChipSx,
    filterAdminTagOptions,
} from "../pzl_common/board/adminTagFilterChipSx";
import { getStoryTemplateSummary } from "../story/storyDisplayUtils";
import StoryImageZoomDialog from "../story/StoryImageZoomDialog";
import AdminJigsawService from "./AdminJigsawService";
import JigsawPlaySummaryRow from "./JigsawPlaySummaryRow";
import AdminStoryService from "../story/AdminStoryService";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_GEN_SIZE = 10;
const PAGE_STORAGE_KEY = "admin.jigsaw.browse.page";
const PAGE_SIZE_STORAGE_KEY = "admin.jigsaw.browse.pageSize";
const STATE_STORAGE_KEY = "admin.jigsaw.browse.state";
const LANGUAGE_STORAGE_KEY = "admin.jigsaw.browse.language";
const TAGS_STORAGE_KEY = "admin.jigsaw.browse.tags";
const KEYWORD_STORAGE_KEY = "admin.jigsaw.browse.keyword";
const SORT_STORAGE_KEY = "admin.jigsaw.browse.sort";
const FREE_STORAGE_KEY = "admin.jigsaw.browse.free";
const STANDALONE_STORAGE_KEY = "admin.jigsaw.browse.standaloneImage";
const VIEW_STORAGE_KEY = "admin.jigsaw.browse.view";
const BROWSE_TAG_STORAGE_KEY = "admin.jigsaw.browse.browseTag";

const ORDER_BY_OPTIONS = [
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
];

const VALID_SORT_VALUES = new Set(ORDER_BY_OPTIONS.map((o) => o.value));
const DEFAULT_SORT = "publishedAt:desc";

const FREE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "true", label: "Free" },
    { value: "false", label: "Paid" },
];

const STANDALONE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "true", label: "Standalone" },
    { value: "false", label: "Not standalone" },
];

const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

function loadPage() {
    try {
        const v = parseInt(localStorage.getItem(PAGE_STORAGE_KEY), 10);
        if (Number.isFinite(v) && v >= 1) return v;
    } catch {}
    return 1;
}

function loadStateFilter() {
    try {
        const v = localStorage.getItem(STATE_STORAGE_KEY);
        if (v === "PUBLISHED" || v === "REVIEWED" || v === "DRAFT" || v === "DISABLED" || v === "DISCARDED" || v === "") return v;
    } catch {}
    return "PUBLISHED";
}

function loadLanguageFilter() {
    try {
        const v = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (v === "" || v === "english" || v === "french") return v;
    } catch {}
    return "";
}

function loadTagsFilter() {
    try {
        const raw = localStorage.getItem(TAGS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
            .filter((tag) => tag.length > 0);
    } catch {}
    return [];
}

function loadKeywordFilter() {
    try {
        const v = localStorage.getItem(KEYWORD_STORAGE_KEY);
        return typeof v === "string" ? v : "";
    } catch {}
    return "";
}

function loadSortFilter() {
    try {
        const v = localStorage.getItem(SORT_STORAGE_KEY);
        if (VALID_SORT_VALUES.has(v)) return v;
    } catch {}
    return DEFAULT_SORT;
}

function loadFreeFilter() {
    try {
        const v = localStorage.getItem(FREE_STORAGE_KEY);
        if (v === "" || v === "true" || v === "false") return v;
    } catch {}
    return "";
}

function loadStandaloneFilter() {
    try {
        const v = localStorage.getItem(STANDALONE_STORAGE_KEY);
        if (v === "" || v === "true" || v === "false") return v;
    } catch {}
    return "";
}

function loadViewMode() {
    try {
        const v = localStorage.getItem(VIEW_STORAGE_KEY);
        if (v === "list" || v === "image" || v === "tags") return v;
    } catch {}
    return "list";
}

function loadBrowseTag() {
    try {
        const v = localStorage.getItem(BROWSE_TAG_STORAGE_KEY);
        return typeof v === "string" ? v.trim() : "";
    } catch {}
    return "";
}

function getBrowseImageUrl(story) {
    if (!story) return null;
    return story.imageUrl ?? story.imageIcon400Url ?? story.imageIcon200Url ?? story.imagePreviewUrl ?? null;
}

function jigsawBrowseBundleState(group) {
    return String(group?.storyJigsaw?.state || "").toUpperCase();
}

function isJigsawBrowseUnavailable(group) {
    const s = jigsawBrowseBundleState(group);
    return s === "DISABLED" || s === "DISCARDED";
}

function jigsawBrowseStateChipSx(state) {
    const s = String(state || "").toUpperCase();
    if (s === "DISABLED" || s === "DISCARDED") {
        return { bgcolor: "grey.400", color: "grey.900", fontWeight: 600 };
    }
    return undefined;
}

function persistPage(nextPage) {
    try {
        localStorage.setItem(PAGE_STORAGE_KEY, String(nextPage));
    } catch {}
}

function loadPageSize() {
    try {
        const n = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10);
        if (PAGE_SIZE_OPTIONS.includes(n)) return n;
    } catch {}
    return DEFAULT_PAGE_SIZE;
}

function persistPageSize(nextSize) {
    try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(nextSize));
    } catch {}
}

function tagOptionLabel(option) {
    const name = typeof option === "string" ? option : (option?.tag ?? option?.name ?? String(option ?? ""));
    const count = typeof option === "object" && option != null ? option.count : undefined;
    if (count != null && count !== "") return `${name} (${count})`;
    return name;
}

function tagOptionValue(option) {
    return typeof option === "string" ? option : (option?.tag ?? option?.name ?? String(option ?? ""));
}

function sortTagOptions(tags) {
    if (!Array.isArray(tags)) return [];
    return [...tags].sort((a, b) =>
        tagOptionValue(a).localeCompare(tagOptionValue(b), undefined, { sensitivity: "base" }),
    );
}

const MAX_JIGSAW_BATCH_SIZE = 1000;

function clampGenSize(value) {
    const n = parseInt(String(value ?? "").trim(), 10);
    if (!Number.isFinite(n)) return DEFAULT_GEN_SIZE;
    return Math.min(MAX_JIGSAW_BATCH_SIZE, Math.max(1, n));
}

function clampEnsureSize(value) {
    return clampGenSize(value);
}

/** Sum of cached color-basket configs (distinct K) across story templates. */
function sumColorBasketCount(templates) {
    if (!Array.isArray(templates) || templates.length === 0) return 0;
    return templates.reduce((sum, tpl) => {
        const n = Number(tpl?.colorBasketCount);
        return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
}

function getStoryTitle(story, preferredLanguage) {
    if (!story) return "";
    const en = story.english?.title ?? story.en?.title ?? "";
    const fr = story.french?.title ?? story.fr?.title ?? "";
    const pref = typeof preferredLanguage === "string" ? preferredLanguage.trim().toLowerCase() : "";
    if (pref === "french") return fr || en || "";
    if (pref === "english") return en || fr || "";
    return en || fr || "";
}

function formatLanguageLabel(language) {
    const s = typeof language === "string" ? language.trim() : "";
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatBrowseDateTime(value) {
    if (value == null || value === "") return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (!Number.isFinite(d.getTime())) return "—";
    return `${d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

function getGroupCreatedAt(story, templates) {
    const fromStory = story?.createdAt ?? story?.created_at ?? null;
    if (fromStory != null && fromStory !== "") return fromStory;
    if (!Array.isArray(templates) || templates.length === 0) return null;
    let latest = null;
    for (const tpl of templates) {
        const raw = tpl?.createdAt ?? tpl?.created_at;
        if (raw == null || raw === "") continue;
        const n = typeof raw === "number" ? raw : new Date(raw).getTime();
        if (!Number.isFinite(n)) continue;
        if (latest == null || n > latest) latest = n;
    }
    return latest;
}

function getJigsawGroupTags(group, story) {
    const fromPuzzleTemplate = group?.puzzleTemplate?.tags ?? group?.puzzle_template?.tags;
    if (Array.isArray(fromPuzzleTemplate)) {
        return fromPuzzleTemplate
            .map((tag) => String(tag).trim())
            .filter((tag) => tag && tag.toLowerCase() !== "bedrock");
    }
    const fromStory = story?.tags;
    if (Array.isArray(fromStory)) {
        return fromStory
            .map((tag) => String(tag).trim())
            .filter((tag) => tag && tag.toLowerCase() !== "bedrock");
    }
    const summary = getStoryTemplateSummary(story);
    if (summary?.tags?.length) {
        return summary.tags.filter((tag) => tag.toLowerCase() !== "bedrock");
    }
    const genre = story?.topic?.genre?.name;
    if (typeof genre === "string" && genre.trim()) {
        const tag = genre.trim();
        return tag.toLowerCase() === "bedrock" ? [] : [tag];
    }
    return [];
}

function applyTagsToGroup(group, tags) {
    const nextTags = Array.isArray(tags) ? tags : [];
    const puzzleTemplate = group?.puzzleTemplate ?? group?.puzzle_template;
    return {
        ...group,
        story: group?.story ? { ...group.story, tags: nextTags } : group?.story,
        puzzleTemplate: { ...(puzzleTemplate || {}), tags: nextTags },
    };
}

function tagButtonLabel(displayTags) {
    if (!displayTags.length) return "Tag";
    if (displayTags.length === 1) return displayTags[0];
    return `${displayTags[0]} +${displayTags.length - 1}`;
}

export default function AdminJigsawMain() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(loadPage);
    const [pageSize, setPageSize] = useState(loadPageSize);
    const [totalPages, setTotalPages] = useState(1);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [savingFreeStoryId, setSavingFreeStoryId] = useState(null);
    const [publishingStoryId, setPublishingStoryId] = useState(null);
    const [markingReviewedStoryId, setMarkingReviewedStoryId] = useState(null);
    const [stateFilter, setStateFilter] = useState(loadStateFilter);
    const [languageFilter, setLanguageFilter] = useState(loadLanguageFilter);
    const [keyword, setKeyword] = useState(loadKeywordFilter);
    const [selectedTags, setSelectedTags] = useState(loadTagsFilter);
    const [sort, setSort] = useState(loadSortFilter);
    const [freeFilter, setFreeFilter] = useState(loadFreeFilter);
    const [standaloneFilter, setStandaloneFilter] = useState(loadStandaloneFilter);
    const [viewMode, setViewMode] = useState(loadViewMode);
    const [browseTag, setBrowseTag] = useState(loadBrowseTag);
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagDialog, setTagDialog] = useState(null);
    const [tagDraft, setTagDraft] = useState([]);
    const [savingTags, setSavingTags] = useState(false);
    const [zoomImage, setZoomImage] = useState(null);
    const [regenDialogOpen, setRegenDialogOpen] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [disabling, setDisabling] = useState(false);

    const sortedAvailableTags = useMemo(
        () => sortTagOptions(availableTags),
        [availableTags],
    );

    const browseTagCount = useMemo(() => {
        if (viewMode !== "tags" || !browseTag) return null;
        const row = sortedAvailableTags.find(
            (option) => tagOptionValue(option).toLowerCase() === browseTag.toLowerCase(),
        );
        return typeof row?.count === "number" && row.count >= 0 ? row.count : null;
    }, [viewMode, browseTag, sortedAvailableTags]);
    const browseTagCountRef = useRef(browseTagCount);
    browseTagCountRef.current = browseTagCount;
    /** Bumped when a newer list request starts or the effect cleans up — ignore stale responses. */
    const fetchSeqRef = useRef(0);

    // Generation dialog state
    const [genDialogOpen, setGenDialogOpen] = useState(false);
    const [genSizeInput, setGenSizeInput] = useState(String(DEFAULT_GEN_SIZE));
    const [genMakeWeakAgo, setGenMakeWeakAgo] = useState(false);
    const [genSubmitting, setGenSubmitting] = useState(false);
    const [genResult, setGenResult] = useState(null);

    const [ensureDialogOpen, setEnsureDialogOpen] = useState(false);
    const [ensureSizeInput, setEnsureSizeInput] = useState(String(DEFAULT_GEN_SIZE));
    const [ensureSubmitting, setEnsureSubmitting] = useState(false);
    const [ensureResult, setEnsureResult] = useState(null);

    const [preImageRefreshDialogOpen, setPreImageRefreshDialogOpen] = useState(false);

    const fetchList = useCallback(async () => {
        const seq = ++fetchSeqRef.current;
        if (viewMode === "tags" && !browseTag) {
            if (seq !== fetchSeqRef.current) return;
            setGroups([]);
            setTotalPages(1);
            setLoading(false);
            return;
        }
        setLoading(true);
        const freeParam =
            freeFilter === "true" ? true : freeFilter === "false" ? false : undefined;
        const standaloneParam =
            standaloneFilter === "true" ? true : standaloneFilter === "false" ? false : undefined;
        const tagsForApi = viewMode === "tags" ? [browseTag] : selectedTags;
        const keywordForApi = viewMode === "tags" ? "" : keyword;
        const result = await AdminJigsawService.list(
            page - 1,
            pageSize,
            sort,
            stateFilter || undefined,
            keywordForApi,
            languageFilter || undefined,
            tagsForApi,
            freeParam,
            standaloneParam,
        );
        if (seq !== fetchSeqRef.current) return;
        setLoading(false);
        if (result.success) {
            const reportedPages = Number(result.data.totalPages);
            const reportedCount = Number(result.data.totalElements);
            const pageItemCount = Array.isArray(result.data.content) ? result.data.content.length : 0;
            const countIsCatalogTotal = Number.isFinite(reportedCount) && reportedCount > pageItemCount;
            let nextTotalPages = Number.isFinite(reportedPages) && reportedPages >= 1
                ? reportedPages
                : (countIsCatalogTotal
                    ? Math.max(1, Math.ceil(reportedCount / pageSize))
                    : null);
            const countForPages = browseTagCountRef.current;
            if (viewMode === "tags" && countForPages != null) {
                nextTotalPages = Math.max(1, Math.ceil(countForPages / pageSize));
            }
            const visibleGroups = (Array.isArray(result.data.content) ? result.data.content : []).filter((group) => {
                if (freeFilter !== "true" && freeFilter !== "false") return true;
                const isFree = group?.storyJigsaw?.free === true;
                return freeFilter === "true" ? isFree : !isFree;
            });
            if (nextTotalPages == null) {
                setGroups(visibleGroups);
                return;
            }
            setTotalPages(nextTotalPages);
            // Filters may shrink the result set while localStorage still has an old page.
            if (page > nextTotalPages) {
                setPage(nextTotalPages);
                persistPage(nextTotalPages);
                return;
            }
            setGroups(visibleGroups);
        } else {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to load" });
        }
    }, [page, pageSize, stateFilter, languageFilter, keyword, selectedTags, sort, freeFilter, standaloneFilter, viewMode, browseTag]);

    const goToFirstPage = useCallback(() => {
        setPage(1);
        persistPage(1);
    }, []);

    const handlePageSizeChange = (e) => {
        const next = Number(e.target.value);
        if (!PAGE_SIZE_OPTIONS.includes(next)) return;
        setPageSize(next);
        persistPageSize(next);
        setPage(1);
        persistPage(1);
    };

    const handleKeywordChange = useCallback((kw) => {
        setKeyword(kw);
        goToFirstPage();
        try {
            localStorage.setItem(KEYWORD_STORAGE_KEY, typeof kw === "string" ? kw : "");
        } catch {}
    }, [goToFirstPage]);

    const handleTagsChange = useCallback((newTags) => {
        setSelectedTags(newTags);
        goToFirstPage();
        try {
            localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(newTags));
        } catch {}
    }, [goToFirstPage]);

    const handleViewModeChange = useCallback((_event, next) => {
        if (!next) return;
        setViewMode(next);
        goToFirstPage();
        try { localStorage.setItem(VIEW_STORAGE_KEY, next); } catch {}
    }, [goToFirstPage]);

    const handleBrowseTagClick = useCallback((tag) => {
        const normalized = String(tag ?? "").trim();
        if (!normalized) return;
        setTotalPages(1);
        setPage(1);
        persistPage(1);
        setBrowseTag((prev) => {
            const next = prev && prev.toLowerCase() === normalized.toLowerCase() ? "" : normalized;
            try { localStorage.setItem(BROWSE_TAG_STORAGE_KEY, next); } catch {}
            return next;
        });
    }, []);

    const showImageGrid = viewMode === "image" || viewMode === "tags";

    const refreshAvailableTags = useCallback(async ({ showLoading = false } = {}) => {
        if (showLoading) setTagsLoading(true);
        const result = await AdminJigsawService.getDistinctTags(
            stateFilter || undefined,
            languageFilter || undefined,
        );
        if (showLoading) setTagsLoading(false);
        if (result.success) {
            setAvailableTags(result.data ?? []);
        }
    }, [stateFilter, languageFilter]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setTagsLoading(true);
            const result = await AdminJigsawService.getDistinctTags(
                stateFilter || undefined,
                languageFilter || undefined,
            );
            if (cancelled) return;
            setTagsLoading(false);
            if (result.success) {
                setAvailableTags(result.data ?? []);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [stateFilter, languageFilter]);

    useEffect(() => {
        fetchList();
        return () => {
            // Invalidate any in-flight list so an older unfiltered response cannot overwrite
            // a newer filtered one (medical → all while the tag chip stays selected).
            fetchSeqRef.current += 1;
        };
    }, [fetchList]);

    // Distinct-tag counts can arrive after the list; update pagination without refetching.
    useEffect(() => {
        if (viewMode !== "tags" || browseTagCount == null) return;
        const pages = Math.max(1, Math.ceil(browseTagCount / pageSize));
        setTotalPages(pages);
        if (page > pages) {
            setPage(pages);
            persistPage(pages);
        }
    }, [viewMode, browseTagCount, pageSize, page]);

    const handleOpenGenDialog = useCallback(() => {
        setGenSizeInput(String(DEFAULT_GEN_SIZE));
        setGenMakeWeakAgo(false);
        setGenDialogOpen(true);
    }, []);

    const handleGenSubmit = useCallback(async () => {
        const size = clampGenSize(genSizeInput);
        setGenSizeInput(String(size));
        setGenSubmitting(true);
        const result = await AdminJigsawService.genJigsawNullStory(size, genMakeWeakAgo);
        setGenSubmitting(false);
        setGenDialogOpen(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Generation failed" });
            return;
        }
        setGenResult(result.data);
    }, [genSizeInput, genMakeWeakAgo]);

    const handleCloseGenResult = useCallback(() => {
        setGenResult(null);
        fetchList();
    }, [fetchList]);

    const handleOpenEnsureDialog = useCallback(() => {
        setEnsureSizeInput(String(DEFAULT_GEN_SIZE));
        setEnsureDialogOpen(true);
    }, []);

    const handleEnsureSubmit = useCallback(async () => {
        const size = clampEnsureSize(ensureSizeInput);
        setEnsureSizeInput(String(size));
        setEnsureSubmitting(true);
        const result = await AdminJigsawService.ensureMissingTemplates(size);
        setEnsureSubmitting(false);
        setEnsureDialogOpen(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Ensure failed" });
            return;
        }
        setEnsureResult(result.data);
    }, [ensureSizeInput]);

    const handleCloseEnsureResult = useCallback(() => {
        setEnsureResult(null);
        fetchList();
    }, [fetchList]);

    const handleOpenPreImageRefreshDialog = useCallback(() => {
        setPreImageRefreshDialogOpen(true);
    }, []);

    const handleClosePreImageRefreshDialog = useCallback(() => {
        setPreImageRefreshDialogOpen(false);
    }, []);

    const startPreImageRefreshJob = useCallback(
        (size, opts) => AdminJigsawService.genPreImageRefreshAsync(size, opts),
        []
    );

    const fetchPreImageRefreshJob = useCallback(
        (jobId) => AdminJigsawService.getPreImageRefreshJob(jobId),
        []
    );

    const cancelPreImageRefreshJob = useCallback(
        (jobId) => AdminJigsawService.cancelPreImageRefreshJob(jobId),
        []
    );

    const handlePublishStory = useCallback(async (storyId, templates) => {
        if (!storyId) return;
        if (!Array.isArray(templates) || templates.length === 0) {
            setSnackbar({ open: true, severity: "error", message: "No jigsaw templates to publish" });
            return;
        }
        setPublishingStoryId(storyId);
        const result = await AdminJigsawService.publishStory(storyId);
        setPublishingStoryId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Publish failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Published successfully" });
        setZoomImage((prev) => (prev?.storyId === storyId ? { ...prev, state: "PUBLISHED" } : prev));
        fetchList();
    }, [fetchList]);

    const handleMarkReviewed = useCallback(async (storyId) => {
        if (!storyId) return;
        setMarkingReviewedStoryId(storyId);
        const result = await AdminJigsawService.markReviewed(storyId);
        setMarkingReviewedStoryId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Mark reviewed failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Marked REVIEWED" });
        fetchList();
    }, [fetchList]);

    const handleStoryFreeChange = useCallback(async (storyId, free) => {
        if (!storyId || savingFreeStoryId === storyId) return;

        const previousGroup = groups.find((g) => g.story?.id === storyId) ?? null;
        const previousFree = previousGroup?.storyJigsaw?.free === true;
        const filterActive = freeFilter === "true" || freeFilter === "false";

        const applyFreeLocally = (nextFree) => {
            setGroups((prev) => {
                let found = false;
                const nextList = prev.flatMap((g) => {
                    if (g.story?.id !== storyId) return [g];
                    found = true;
                    const next = {
                        ...g,
                        storyJigsaw: { ...(g.storyJigsaw || {}), free: nextFree },
                    };
                    if (freeFilter === "true" && !nextFree) return [];
                    if (freeFilter === "false" && nextFree) return [];
                    return [next];
                });
                // Restore a row that was dropped by the Access filter (e.g. API error revert).
                if (!found && previousGroup && (
                    (freeFilter === "true" && nextFree)
                    || (freeFilter === "false" && !nextFree)
                    || !filterActive
                )) {
                    return [
                        ...prev,
                        {
                            ...previousGroup,
                            storyJigsaw: { ...(previousGroup.storyJigsaw || {}), free: nextFree },
                        },
                    ];
                }
                return nextList;
            });
            setZoomImage((prev) => (prev?.storyId === storyId ? { ...prev, free: nextFree } : prev));
        };

        // Optimistic: flip the checkbox immediately, then confirm with the API.
        applyFreeLocally(free);
        setSavingFreeStoryId(storyId);
        try {
            const result = await AdminJigsawService.setStoryFree(storyId, free);
            if (!result.success) {
                applyFreeLocally(previousFree);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Failed to update free flag",
                });
                return;
            }
            if (typeof result.free === "boolean" && result.free !== free) {
                applyFreeLocally(result.free);
            }
        } catch (e) {
            applyFreeLocally(previousFree);
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Failed to update free flag",
            });
        } finally {
            setSavingFreeStoryId(null);
        }
    }, [freeFilter, groups, savingFreeStoryId]);

    const openTagDialog = useCallback((group) => {
        const story = group?.story;
        const storyId = story?.id;
        if (!storyId) return;
        setTagDialog({
            storyId,
            title: getStoryTitle(story, languageFilter) || storyId,
        });
        setTagDraft(getJigsawGroupTags(group, story));
    }, [languageFilter]);

    const handleCloseTagDialog = useCallback(() => {
        if (savingTags) return;
        setTagDialog(null);
        setTagDraft([]);
    }, [savingTags]);

    const handleSaveTags = useCallback(async () => {
        const storyId = tagDialog?.storyId;
        if (!storyId || savingTags) return;
        const nextTags = tagDraft
            .map((tag) => tagOptionValue(tag).trim())
            .filter((tag) => tag.length > 0);
        setSavingTags(true);
        const result = await AdminJigsawService.setStoryTags(storyId, nextTags);
        setSavingTags(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update tags" });
            return;
        }
        const stillMatchesFilter = (() => {
            if (viewMode === "tags") {
                if (!browseTag) return true;
                return nextTags.some((tag) => tag.toLowerCase() === browseTag.toLowerCase());
            }
            if (!Array.isArray(selectedTags) || selectedTags.length === 0) return true;
            const wanted = selectedTags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
            return nextTags.some((tag) => wanted.includes(tag.toLowerCase()));
        })();
        setGroups((prev) => prev
            .map((g) => (g.story?.id === storyId ? applyTagsToGroup(g, nextTags) : g))
            .filter((g) => g.story?.id !== storyId || stillMatchesFilter));
        setTagDialog(null);
        setTagDraft([]);
        setSnackbar({ open: true, severity: "success", message: "Tags updated" });
        refreshAvailableTags();
    }, [tagDialog, tagDraft, savingTags, viewMode, browseTag, selectedTags, refreshAvailableTags]);

    const handlePickTagFromList = useCallback((tagName) => {
        const name = String(tagName || "").trim();
        if (!name) return;
        setTagDraft((prev) => {
            const has = prev.some((t) => tagOptionValue(t).toLowerCase() === name.toLowerCase());
            if (has) return prev.filter((t) => tagOptionValue(t).toLowerCase() !== name.toLowerCase());
            return [name];
        });
    }, []);

    const handleConfirmRegenImage = useCallback(async (storyId) => {
        if (!storyId || regenLoading) return;
        setRegenLoading(true);
        try {
            const result = await AdminStoryService.regenImage(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Regenerate image failed",
                });
                return;
            }
            const nextUrl = getBrowseImageUrl(result.data) || zoomImage?.url || "";
            const busted = nextUrl
                ? `${nextUrl}${nextUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
                : "";
            if (busted) {
                setZoomImage((prev) => (prev ? { ...prev, url: busted } : prev));
            }
            const preview = await AdminJigsawService.genPreview(storyId);
            setSnackbar({
                open: true,
                severity: preview.success ? "success" : "warning",
                message: preview.success
                    ? "Image replaced. Jigsaw puzzle images were regenerated."
                    : "Image replaced. Jigsaw preview refresh failed — use Gen preview if needed.",
            });
            await fetchList();
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Regenerate image failed",
            });
        } finally {
            setRegenLoading(false);
        }
    }, [fetchList, regenLoading, zoomImage?.url]);

    const handleConfirmDelete = useCallback(async (storyId) => {
        if (!storyId || deleting) return;
        setDeleting(true);
        try {
            const result = await AdminJigsawService.removeByStoryId(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Delete failed",
                });
                return;
            }
            setZoomImage(null);
            setSnackbar({ open: true, severity: "success", message: "Jigsaw deleted" });
            await fetchList();
            refreshAvailableTags();
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Delete failed",
            });
        } finally {
            setDeleting(false);
        }
    }, [deleting, fetchList, refreshAvailableTags]);

    const handleConfirmDisable = useCallback(async (storyId) => {
        if (!storyId || disabling) return;
        setDisabling(true);
        try {
            const result = await AdminJigsawService.disableStory(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Disable failed",
                });
                return;
            }
            setZoomImage((prev) => (prev ? { ...prev, state: "DISABLED" } : prev));
            setSnackbar({
                open: true,
                severity: "success",
                message: "Puzzle disabled. Players cannot access it. It will be discarded in 1 week.",
            });
            await fetchList();
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Disable failed",
            });
        } finally {
            setDisabling(false);
        }
    }, [disabling, fetchList]);

    const COL_SPAN = 8;

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1} sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1, width: "100%" }}>
                    <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="flex-end"
                    >
                        <Button variant="outlined" color="primary" onClick={handleOpenGenDialog}>
                            Generate Jigsaw puzzles
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleOpenEnsureDialog}>
                            Ensure missing templates
                        </Button>
                        <Button variant="outlined" color="primary" onClick={handleOpenPreImageRefreshDialog}>
                            Gen preview image(refresh)
                        </Button>
                    </Stack>
                </Paper>
                <Typography variant="h5">Jigsaw puzzles</Typography>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={viewMode}
                        onChange={handleViewModeChange}
                        aria-label="Browse view"
                    >
                        <ToggleButton value="list" aria-label="List view" title="List" sx={{ px: 1 }}>
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="image" aria-label="Image view" title="Image" sx={{ px: 1 }}>
                            <ViewModuleIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="tags" aria-label="Browse by tag" title="Browse by tag" sx={{ px: 1 }}>
                            <LocalOfferOutlinedIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={languageFilter}
                            label="Language"
                            onChange={(e) => {
                                setLanguageFilter(e.target.value);
                                goToFirstPage();
                                try { localStorage.setItem(LANGUAGE_STORAGE_KEY, e.target.value); } catch {}
                            }}
                        >
                            {LANGUAGE_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>State</InputLabel>
                        <Select
                            value={stateFilter}
                            label="State"
                            onChange={(e) => {
                                setStateFilter(e.target.value);
                                goToFirstPage();
                                try { localStorage.setItem(STATE_STORAGE_KEY, e.target.value); } catch {}
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="PUBLISHED">Published</MenuItem>
                            <MenuItem value="REVIEWED">Reviewed</MenuItem>
                            <MenuItem value="DRAFT">Draft</MenuItem>
                            <MenuItem value="DISABLED">Disabled</MenuItem>
                            <MenuItem value="DISCARDED">Discarded</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="jigsaw-order-by-label">Order by</InputLabel>
                        <Select
                            labelId="jigsaw-order-by-label"
                            value={sort}
                            label="Order by"
                            onChange={(e) => {
                                const next = e.target.value;
                                setSort(next);
                                goToFirstPage();
                                try { localStorage.setItem(SORT_STORAGE_KEY, next); } catch {}
                            }}
                        >
                            {ORDER_BY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="jigsaw-free-filter-label">Access</InputLabel>
                        <Select
                            labelId="jigsaw-free-filter-label"
                            value={freeFilter}
                            label="Access"
                            onChange={(e) => {
                                const next = e.target.value;
                                setFreeFilter(next);
                                goToFirstPage();
                                try { localStorage.setItem(FREE_STORAGE_KEY, next); } catch {}
                            }}
                        >
                            {FREE_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="jigsaw-standalone-filter-label">Standalone</InputLabel>
                        <Select
                            labelId="jigsaw-standalone-filter-label"
                            value={standaloneFilter}
                            label="Standalone"
                            onChange={(e) => {
                                const next = e.target.value;
                                setStandaloneFilter(next);
                                goToFirstPage();
                                try { localStorage.setItem(STANDALONE_STORAGE_KEY, next); } catch {}
                            }}
                        >
                            {STANDALONE_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {viewMode !== "tags" ? (
                        <>
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        disableCloseOnSelect
                        options={sortedAvailableTags}
                        value={selectedTags.map(
                            (s) => sortedAvailableTags.find((o) => tagOptionValue(o).toLowerCase() === s.toLowerCase()) ?? s,
                        )}
                        isOptionEqualToValue={(opt, val) =>
                            tagOptionValue(opt).toLowerCase() === tagOptionValue(val).toLowerCase()
                        }
                        getOptionKey={(option) => tagOptionValue(option).toLowerCase()}
                        filterOptions={(options, params) =>
                            sortTagOptions(filterAdminTagOptions(options, params, tagOptionValue, selectedTags))
                        }
                        onChange={(_event, newValue) => {
                            handleTagsChange(
                                newValue.map((tag) => tagOptionValue(tag).trim()).filter((tag) => tag.length > 0),
                            );
                        }}
                        loading={tagsLoading}
                        noOptionsText={tagsLoading ? "Loading tags…" : "Type a tag and press Enter"}
                        slotProps={adminTagFilterAutocompleteSlotProps}
                        sx={{
                            minWidth: 200,
                            maxWidth: 280,
                            width: { xs: "100%", sm: 240 },
                        }}
                        getOptionLabel={tagOptionLabel}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={tagOptionValue(option)}
                                    {...getTagProps({ index })}
                                    sx={adminTagFilterChipSx}
                                    key={`${tagOptionValue(option)}-${index}`}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Tags" placeholder="Type or pick a tag" />
                        )}
                    />
                    <KeywordBox
                        callback={handleKeywordChange}
                        initialValue={keyword}
                        label="Search"
                        placeholder="Search by title"
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 220 }}
                    />
                        </>
                    ) : null}
                </Stack>
                {viewMode === "tags" ? (
                    <Paper variant="outlined" sx={{ p: 1.25, width: "100%" }}>
                        {tagsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : sortedAvailableTags.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No tags for the current filters.
                            </Typography>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                {sortedAvailableTags.map((option) => {
                                    const tag = tagOptionValue(option);
                                    const selected = browseTag
                                        && browseTag.toLowerCase() === tag.toLowerCase();
                                    return (
                                        <Chip
                                            key={tag.toLowerCase()}
                                            label={tagOptionLabel(option)}
                                            onClick={() => handleBrowseTagClick(tag)}
                                            color={selected ? "primary" : "default"}
                                            variant={selected ? "filled" : "outlined"}
                                            sx={{ ...adminTagFilterChipSx, cursor: "pointer" }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                    </Paper>
                ) : null}
            </Stack>

            {/* Generate dialog */}
            <Dialog open={genDialogOpen} onClose={() => { if (!genSubmitting) setGenDialogOpen(false); }} maxWidth="xs" fullWidth>
                <DialogTitle>Generate Jigsaw puzzles</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Batch size"
                        type="number"
                        fullWidth
                        value={genSizeInput}
                        onChange={(e) => setGenSizeInput(e.target.value)}
                        disabled={genSubmitting}
                        slotProps={{ htmlInput: { min: 1, max: MAX_JIGSAW_BATCH_SIZE } }}
                        helperText={`Maximum stories to process (1–${MAX_JIGSAW_BATCH_SIZE}). Default 10.`}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={genMakeWeakAgo}
                                onChange={(_, checked) => setGenMakeWeakAgo(checked)}
                                disabled={genSubmitting}
                            />
                        }
                        label="Make weak ago"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGenDialogOpen(false)} disabled={genSubmitting}>Cancel</Button>
                    <Button onClick={handleGenSubmit} variant="contained" disabled={genSubmitting}>
                        {genSubmitting ? <CircularProgress size={22} /> : "Generate"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ensure missing templates dialog */}
            <Dialog open={ensureDialogOpen} onClose={() => { if (!ensureSubmitting) setEnsureDialogOpen(false); }} maxWidth="xs" fullWidth>
                <DialogTitle>Ensure missing jigsaw templates</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Adds missing <strong>jigsaw</strong> rows for <strong>REVIEWED</strong> and{" "}
                        <strong>PUBLISHED</strong> stories when new enabled reusable templates exist. Safe to run
                        more than once.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Batch size"
                        type="number"
                        fullWidth
                        value={ensureSizeInput}
                        onChange={(e) => setEnsureSizeInput(e.target.value)}
                        disabled={ensureSubmitting}
                        slotProps={{ htmlInput: { min: 1, max: MAX_JIGSAW_BATCH_SIZE } }}
                        helperText={`Maximum stories to process (1–${MAX_JIGSAW_BATCH_SIZE}). Default 10.`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEnsureDialogOpen(false)} disabled={ensureSubmitting}>Cancel</Button>
                    <Button onClick={handleEnsureSubmit} variant="contained" disabled={ensureSubmitting}>
                        {ensureSubmitting ? <CircularProgress size={22} /> : "Ensure"}
                    </Button>
                </DialogActions>
            </Dialog>

            <PreviewRefreshJobDialog
                open={preImageRefreshDialogOpen}
                onClose={handleClosePreImageRefreshDialog}
                title="Gen preview image(refresh)"
                description={
                    <>
                        Regenerates share preview and layout PNGs for <strong>REVIEWED</strong> and{" "}
                        <strong>PUBLISHED</strong> stories that already have assets, overwriting the S3 files
                        with the current renderer policy. Rendering runs on the server, so progress is polled
                        here instead of blocking the request.
                    </>
                }
                maxSize={MAX_JIGSAW_BATCH_SIZE}
                startJobFn={startPreImageRefreshJob}
                fetchJobFn={fetchPreImageRefreshJob}
                cancelJobFn={cancelPreImageRefreshJob}
                onComplete={fetchList}
            />

            {/* Ensure result dialog */}
            <Dialog open={Boolean(ensureResult)} onClose={handleCloseEnsureResult} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle>Ensure result</DialogTitle>
                <DialogContent dividers>
                    {ensureResult ? (
                        <Stack spacing={1.5}>
                            <Typography variant="body2"><strong>Candidates:</strong> {ensureResult.candidates ?? "—"}</Typography>
                            <Typography variant="body2"><strong>Processed:</strong> {ensureResult.processed ?? "—"}</Typography>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Results</Typography>
                                {Array.isArray(ensureResult.results) && ensureResult.results.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {ensureResult.results.map((row, idx) => (
                                            <Typography component="li" variant="body2" key={`ensure-${row?.storyId}-${idx}`}>
                                                {row?.storyId ?? "—"}
                                                {row?.templatesCreated != null ? ` — rows created: ${row.templatesCreated}` : ""}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No result rows.</Typography>
                                )}
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Errors</Typography>
                                {Array.isArray(ensureResult.errors) && ensureResult.errors.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {ensureResult.errors.map((err, idx) => (
                                            <Typography component="li" variant="body2" color="error" key={`ensure-err-${idx}`}>
                                                {String(err)}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">None</Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEnsureResult} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Generation result dialog */}
            <Dialog open={Boolean(genResult)} onClose={handleCloseGenResult} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle>Generation result</DialogTitle>
                <DialogContent dividers>
                    {genResult ? (
                        <Stack spacing={1.5}>
                            <Typography variant="body2"><strong>Candidates:</strong> {genResult.candidates ?? "—"}</Typography>
                            <Typography variant="body2"><strong>Processed:</strong> {genResult.processed ?? "—"}</Typography>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Results</Typography>
                                {Array.isArray(genResult.results) && genResult.results.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {genResult.results.map((row, idx) => (
                                            <Typography component="li" variant="body2" key={`${row?.storyId}-${idx}`}>
                                                {row?.storyId ?? "—"}{row?.templatesCreated != null ? ` — templates created: ${row.templatesCreated}` : ""}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No result rows.</Typography>
                                )}
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Errors</Typography>
                                {Array.isArray(genResult.errors) && genResult.errors.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {genResult.errors.map((err, idx) => (
                                            <Typography component="li" variant="body2" color="error" key={`err-${idx}`}>
                                                {String(err)}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">None</Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGenResult} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(tagDialog)} onClose={handleCloseTagDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Update tag</DialogTitle>
                <DialogContent dividers>
                    {tagDialog?.title ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tagDialog.title}
                        </Typography>
                    ) : null}
                    {tagsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : sortedAvailableTags.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No tags available.
                        </Typography>
                    ) : (
                        <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
                            {sortedAvailableTags.map((option) => {
                                const name = tagOptionValue(option);
                                if (!name) return null;
                                const selected = tagDraft.some(
                                    (t) => tagOptionValue(t).toLowerCase() === name.toLowerCase(),
                                );
                                return (
                                    <ListItemButton
                                        key={name}
                                        selected={selected}
                                        disabled={savingTags}
                                        onClick={() => handlePickTagFromList(name)}
                                        sx={{ minHeight: 44 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {selected ? <CheckIcon fontSize="small" color="success" /> : null}
                                        </ListItemIcon>
                                        <ListItemText primary={tagOptionLabel(option)} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTagDialog} disabled={savingTags}>Cancel</Button>
                    <Button onClick={handleSaveTags} variant="contained" disabled={savingTags}>
                        {savingTags ? <CircularProgress size={22} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {showImageGrid ? (
                <Box>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : groups.length === 0 ? (
                        <Paper sx={{ py: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">No jigsaw groups found.</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={1.5}>
                            {groups.map((group, idx) => {
                                const story = group.story;
                                const storyId = story?.id;
                                const imageUrl = getBrowseImageUrl(story);
                                const title = getStoryTitle(story, languageFilter) || storyId || "Jigsaw";
                                const displayTags = getJigsawGroupTags(group, story);
                                const bundleState = jigsawBrowseBundleState(group);
                                const isUnavailableBundle = isJigsawBrowseUnavailable(group);
                                const isDiscardedBundle = bundleState === "DISCARDED";
                                const isFree = group.storyJigsaw?.free === true;
                                const isPacked = group.storyJigsaw?.packed === true;
                                return (
                                    <Grid key={storyId || idx} size={{ xs: 6, sm: 4, md: 3 }}>
                                        <Box>
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                title={title}
                                                sx={{ fontWeight: 600, lineHeight: 1.3, minWidth: 0, flex: 1 }}
                                            >
                                                {title}
                                            </Typography>
                                            {isUnavailableBundle ? (
                                                <Chip
                                                    size="small"
                                                    label={isDiscardedBundle ? "DISCARDED" : "DISABLED"}
                                                    sx={{
                                                        ...jigsawBrowseStateChipSx(bundleState),
                                                        height: 20,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            ) : null}
                                            </Stack>
                                            <Box
                                                onClick={() => storyId && navigate(`/admin/jigsaw/browse/${encodeURIComponent(storyId)}`)}
                                                sx={{
                                                    position: "relative",
                                                    width: "100%",
                                                    aspectRatio: "1 / 1",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    border: 1,
                                                    borderColor: "divider",
                                                    bgcolor: "grey.900",
                                                    cursor: storyId ? "pointer" : "default",
                                                }}
                                            >
                                                {imageUrl ? (
                                                    <Box
                                                        component="img"
                                                        src={imageUrl}
                                                        alt={title}
                                                        sx={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            display: "block",
                                                            filter: isUnavailableBundle ? "grayscale(1)" : "none",
                                                            opacity: isUnavailableBundle ? 0.72 : 1,
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            height: "100%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="grey.500">
                                                            No image
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {isUnavailableBundle ? (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            bgcolor: "rgba(60,60,60,0.55)",
                                                            px: 1,
                                                            pointerEvents: "none",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: "common.white",
                                                                fontWeight: 700,
                                                                textAlign: "center",
                                                                lineHeight: 1.25,
                                                            }}
                                                        >
                                                            {isDiscardedBundle ? "Discarded" : "Not available"}
                                                        </Typography>
                                                        {!isDiscardedBundle ? (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: "common.white", textAlign: "center" }}
                                                            >
                                                                Will be removed in 1 week
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                ) : null}
                                                {isPacked || isFree ? (
                                                <Chip
                                                    size="small"
                                                    label={isPacked ? "Packed" : "Free"}
                                                    color={isPacked ? "warning" : "success"}
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,
                                                        right: 8,
                                                        zIndex: 2,
                                                        fontWeight: 700,
                                                        pointerEvents: "none",
                                                    }}
                                                />
                                                ) : null}
                                            </Box>
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<LocalOfferOutlinedIcon />}
                                                disabled={!storyId}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openTagDialog(group);
                                                }}
                                                sx={{
                                                    minHeight: 44,
                                                    textTransform: "none",
                                                    justifyContent: "flex-start",
                                                    px: 1,
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                                                >
                                                    {tagButtonLabel(displayTags)}
                                                </Typography>
                                            </Button>
                                            <Tooltip title="Zoom image">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Zoom ${title}`}
                                                        disabled={!imageUrl}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!imageUrl) return;
                                                            setZoomImage({
                                                                url: imageUrl,
                                                                title,
                                                                storyId,
                                                                state: bundleState,
                                                                free: isFree,
                                                                standalone: Boolean(story?.standaloneImage),
                                                                templates: group?.templates,
                                                            });
                                                        }}
                                                        sx={{ width: 44, height: 44, flexShrink: 0 }}
                                                    >
                                                        <ZoomInIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            ) : (
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 300 }}>Story</TableCell>
                                <TableCell sx={{ minWidth: 100 }}>State</TableCell>
                                <TableCell sx={{ minWidth: 80 }}>Free</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Created</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Published</TableCell>
                                <TableCell sx={{ minWidth: 100 }}>Templates</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Color Baskets</TableCell>
                                <TableCell sx={{ minWidth: 72, width: 72 }} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={COL_SPAN} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
                            ) : groups.length === 0 ? (
                                <TableRow><TableCell colSpan={COL_SPAN} align="center" sx={{ py: 4 }}>No jigsaw groups found.</TableCell></TableRow>
                            ) : (
                                groups.map((group, idx) => {
                                    const story = group.story;
                                    const storyId = story?.id;
                                    const sj = group.storyJigsaw;
                                    const templates = group.templates ?? [];
                                    const imageUrl = story?.imageIcon200Url ?? story?.imageUrl ?? null;
                                    const pubStr = formatBrowseDateTime(sj?.publishedAt);
                                    const createdStr = formatBrowseDateTime(getGroupCreatedAt(story, templates));
                                    const bundleState = sj?.state ?? "";
                                    const standalone = Boolean(story?.standaloneImage);
                                    const canPublish =
                                        templates.length > 0 &&
                                        (bundleState === "REVIEWED" ||
                                            bundleState === "DISABLED" ||
                                            (standalone && (bundleState === "DRAFT" || bundleState === "REVIEWED")));
                                    const canMarkReviewed = bundleState === "DRAFT" && templates.length > 0 && !standalone;
                                    const isPublishing = publishingStoryId === storyId;
                                    const isMarkingReviewed = markingReviewedStoryId === storyId;
                                    const displayTags = getJigsawGroupTags(group, story);
                                    const languageLabel = formatLanguageLabel(story?.language);

                                    return (
                                        <TableRow
                                            key={storyId || idx}
                                            hover
                                            onClick={() => storyId && navigate(`/admin/jigsaw/browse/${encodeURIComponent(storyId)}`)}
                                            sx={{ cursor: storyId ? "pointer" : "default" }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                    {imageUrl && (
                                                        <Box
                                                            component="img"
                                                            src={imageUrl}
                                                            alt=""
                                                            sx={{
                                                                width: 96,
                                                                height: 96,
                                                                objectFit: "cover",
                                                                borderRadius: 1,
                                                                border: 1,
                                                                borderColor: "divider",
                                                                flexShrink: 0,
                                                                filter: isJigsawBrowseUnavailable(group)
                                                                    ? "grayscale(1)"
                                                                    : "none",
                                                                opacity: isJigsawBrowseUnavailable(group) ? 0.72 : 1,
                                                            }}
                                                        />
                                                    )}
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{getStoryTitle(story, languageFilter) || storyId || "—"}</Typography>
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                                {languageLabel ? (
                                                                    <Chip size="small" variant="outlined" label={languageLabel} />
                                                                ) : null}
                                                                {displayTags.map((tag) => (
                                                                    <Chip
                                                                        key={tag}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={tag}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openTagDialog(group);
                                                                        }}
                                                                    />
                                                                ))}
                                                                {displayTags.length === 0 ? (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        icon={<LocalOfferOutlinedIcon />}
                                                                        label="Tag"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openTagDialog(group);
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                {standalone ? (
                                                                    <Chip size="small" color="info" variant="outlined" label="Standalone image" />
                                                                ) : null}
                                                                {imageUrl ? (
                                                                    <Tooltip title="Zoom image">
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                aria-label={`Zoom ${getStoryTitle(story, languageFilter) || storyId || "image"}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setZoomImage({
                                                                                        url: getBrowseImageUrl(story) || imageUrl,
                                                                                        title: getStoryTitle(story, languageFilter) || storyId || "Jigsaw",
                                                                                        storyId,
                                                                                        state: bundleState,
                                                                                        free: sj?.free === true,
                                                                                        standalone,
                                                                                        templates,
                                                                                    });
                                                                                }}
                                                                                sx={{ width: 44, height: 44 }}
                                                                            >
                                                                                <ZoomInIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                ) : null}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {sj?.state ? (
                                                    <Chip
                                                        size="small"
                                                        label={sj.state}
                                                        color={sj.state === "PUBLISHED" ? "success" : "default"}
                                                        variant={
                                                            sj.state === "PUBLISHED" ||
                                                            ["DISABLED", "DISCARDED"].includes(String(sj.state).toUpperCase())
                                                                ? "filled"
                                                                : "outlined"
                                                        }
                                                        sx={jigsawBrowseStateChipSx(sj.state)}
                                                    />
                                                ) : "—"}
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={sj?.free === true}
                                                        disabled={!storyId || savingFreeStoryId === storyId}
                                                        onChange={(e) => {
                                                            void handleStoryFreeChange(storyId, e.target.checked);
                                                        }}
                                                        inputProps={{ "aria-label": sj?.free === true ? "Free, click to set paid" : "Paid, click to set free" }}
                                                    />
                                                    {sj?.packed ? (
                                                        <Chip size="small" label="Packed" color="warning" />
                                                    ) : null}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">{createdStr}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">{pubStr}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{templates.length}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{sumColorBasketCount(templates)}</Typography>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {canMarkReviewed ? (
                                                    <Tooltip title="Mark REVIEWED">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                aria-label="Mark jigsaw story reviewed"
                                                                disabled={isMarkingReviewed}
                                                                onClick={() => handleMarkReviewed(storyId)}
                                                            >
                                                                {isMarkingReviewed ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <RateReviewIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : canPublish ? (
                                                    <Tooltip title="Publish">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                aria-label="Publish jigsaw story"
                                                                disabled={isPublishing}
                                                                onClick={() => handlePublishStory(storyId, templates)}
                                                            >
                                                                {isPublishing ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <PublishIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : bundleState === "PUBLISHED" ? (
                                                    <Tooltip title="Already published">
                                                        <CheckCircleIcon fontSize="small" color="success" />
                                                    </Tooltip>
                                                ) : bundleState === "DISABLED" ? (
                                                    <Tooltip title="Disabled — not available in the app. Discarded after 1 week.">
                                                        <Typography variant="caption" sx={{ color: "grey.600", fontWeight: 600 }}>
                                                            Disabled
                                                        </Typography>
                                                    </Tooltip>
                                                ) : bundleState === "DISCARDED" ? (
                                                    <Tooltip title="Discarded — no longer available in the app">
                                                        <Typography variant="caption" sx={{ color: "grey.600", fontWeight: 600 }}>
                                                            Discarded
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            )}

            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ mt: 2 }}
            >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="jigsaw-browse-page-size-label">Page size</InputLabel>
                    <Select
                        labelId="jigsaw-browse-page-size-label"
                        label="Page size"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Pagination
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, v) => {
                        setPage(v);
                        persistPage(v);
                    }}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Stack>

            <ConfirmDialog
                open={regenDialogOpen}
                setOpen={setRegenDialogOpen}
                title="Regenerate image?"
                message="This replaces the current illustration and regenerates jigsaw puzzle images. Story text is not changed."
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This jigsaw"
                }
                targetId={zoomImage?.storyId}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Delete jigsaw"
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This jigsaw"
                }
                targetId={zoomImage?.storyId}
                message={[
                    "This permanently deletes the story record, the story image, all jigsaw templates, and the related sliding puzzle.",
                    "Facebook and Instagram posts of this image are removed when post ids are known.",
                    "The crossword or word-search puzzle is not deleted. Scheduled jobs will not recreate this story or its sliding/jigsaw puzzles.",
                    "This action cannot be undone.",
                ]}
                callback={handleConfirmDelete}
            />

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable this puzzle?"
                message="Players will not be able to open or continue this puzzle. It stays disabled for 1 week, then it is discarded."
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This jigsaw"
                }
                targetId={zoomImage?.storyId}
                callback={handleConfirmDisable}
                warningAlert="Not available. Will be removed in 1 week."
                okText="Disable"
                cancelText="Cancel"
            />

            <StoryImageZoomDialog
                open={Boolean(zoomImage?.url)}
                imageUrl={zoomImage?.url || ""}
                title={zoomImage?.title}
                onClose={() => !regenLoading && !deleting && !disabling && setZoomImage(null)}
                onDelete={zoomImage?.storyId ? () => setDeleteDialogOpen(true) : undefined}
                deleting={deleting}
                onDisable={
                    zoomImage?.storyId && String(zoomImage?.state || "").toUpperCase() === "PUBLISHED"
                        ? () => setDisableDialogOpen(true)
                        : undefined
                }
                disabling={disabling}
                onRegen={zoomImage?.storyId ? () => setRegenDialogOpen(true) : undefined}
                regenLoading={regenLoading}
                free={zoomImage?.free === true}
                onFreeChange={
                    zoomImage?.storyId
                        ? (checked) => {
                            void handleStoryFreeChange(zoomImage.storyId, checked);
                        }
                        : undefined
                }
                savingFree={savingFreeStoryId === zoomImage?.storyId}
                state={zoomImage?.state}
                standalone={zoomImage?.standalone === true}
                allowPublishDisabled
                onPublish={
                    zoomImage?.storyId
                        ? () => handlePublishStory(zoomImage.storyId, zoomImage.templates)
                        : undefined
                }
                publishing={publishingStoryId === zoomImage?.storyId}
                extra={
                    zoomImage?.storyId ? (
                        <JigsawPlaySummaryRow storyId={zoomImage.storyId} />
                    ) : null
                }
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(o) => setSnackbar((s) => ({ ...s, open: o }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
